import { Card } from "@/components/ui/card";
import { User, FileText, FileSignature, CreditCard, ChevronDown } from "lucide-react";

/**
 * Onboarding Status Dashboard
 *
 * Horizontal row of 4 status cards showing the customer onboarding pipeline at a glance:
 *  - Customer (name + business captured)
 *  - Contract (loan agreement generated)
 *  - E-signature (DocuSign envelope status)
 *  - Direct Debit (GoCardless mandate)
 *
 * Each tile is clickable — clicking expands the matching drill-down panel
 * (rendered by the parent below this component).
 */
export default function OnboardingStatusDashboard({
  customer,
  business,
  sfaNumber,
  generatedDoc,
  docuSignResult,
  gcResult,
  envelopes = [],
  activeTile = null,
  onTileClick,
  comingSoonTiles = [],
  ddLabel = "Direct Debit",
}) {
  // Customer card
  const customerName =
    typeof customer === "object"
      ? customer?.name || customer?.full_name || ""
      : customer || "";
  const customerComplete = !!(customerName && business);

  // Contract card
  const contractStatus = generatedDoc?.file_url
    ? "generated"
    : "not_started";

  // E-signature card — use the latest envelope if available
  const latestEnvelope = envelopes[0];
  const docuSignStatus = (() => {
    if (latestEnvelope?.status) return latestEnvelope.status; // 'sent', 'delivered', 'completed', 'declined'
    if (docuSignResult?.envelope_id) return "sent";
    return "not_started";
  })();

  // Direct debit card
  const gcStatus = (() => {
    if (gcResult?.billing_request_id) {
      // Could be 'pending', 'ready_to_fulfil', 'fulfilled' etc — keep simple here
      return gcResult.status || "pending";
    }
    return "not_started";
  })();

  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3"
      data-testid="onboarding-status-dashboard"
    >
      <StatusCard
        tileId="customer"
        active={activeTile === "customer"}
        onClick={onTileClick}
        icon={User}
        label="Customer"
        primary={customerName || "—"}
        secondary={business || (customerName ? "Add business" : "Not started")}
        status={customerComplete ? "complete" : customerName ? "in_progress" : "not_started"}
        statusLabel={
          customerComplete
            ? "Complete"
            : customerName
              ? "Partial"
              : "Not started"
        }
        accent="indigo"
      />
      <StatusCard
        tileId="contract"
        active={activeTile === "contract"}
        onClick={onTileClick}
        icon={FileText}
        label="Contract"
        primary={sfaNumber || (contractStatus === "generated" ? "Generated" : "—")}
        secondary={
          contractStatus === "generated"
            ? "Loan agreement ready"
            : "Not yet generated"
        }
        status={contractStatus === "generated" ? "complete" : "not_started"}
        statusLabel={
          contractStatus === "generated" ? "Generated" : "Not started"
        }
        accent="blue"
      />
      <StatusCard
        tileId="esign"
        active={activeTile === "esign"}
        onClick={onTileClick}
        icon={FileSignature}
        label="E-signature"
        primary={
          docuSignResult?.envelope_id?.slice(0, 8) ||
          latestEnvelope?.envelope_id?.slice(0, 8) ||
          "—"
        }
        secondary={signatureSecondary(docuSignStatus, latestEnvelope)}
        status={signatureToBucket(docuSignStatus)}
        statusLabel={signatureLabel(docuSignStatus)}
        accent="violet"
      />
      <StatusCard
        tileId="dd"
        active={activeTile === "dd"}
        onClick={onTileClick}
        comingSoon={comingSoonTiles.includes("dd")}
        icon={CreditCard}
        label={ddLabel}
        primary={
          comingSoonTiles.includes("dd")
            ? "—"
            : gcResult?.scheme === "au_pay_to"
              ? "PayTo"
              : gcResult?.scheme === "becs"
                ? "BECS Direct Debit"
                : "—"
        }
        secondary={
          comingSoonTiles.includes("dd")
            ? "GoCardless integration coming soon"
            : gcResult?.billing_request_id
              ? `Ref ${gcResult.billing_request_id.slice(0, 12)}…`
              : "Mandate not created"
        }
        status={
          comingSoonTiles.includes("dd")
            ? "coming_soon"
            : gcStatus === "fulfilled"
              ? "complete"
              : gcResult
                ? "in_progress"
                : "not_started"
        }
        statusLabel={
          comingSoonTiles.includes("dd")
            ? "Coming soon"
            : gcStatus === "fulfilled"
              ? "Authorised"
              : gcResult
                ? "Awaiting authorisation"
                : "Not started"
        }
        accent="emerald"
      />
    </div>
  );
}

function signatureSecondary(status, latestEnvelope) {
  if (status === "completed" || status === "signed") return "Signed by all parties";
  if (status === "declined" || status === "voided") return "Declined / voided";
  if (status === "delivered") return "Awaiting signature";
  if (status === "sent") return "Sent for signing";
  if (latestEnvelope?.subject) return latestEnvelope.subject;
  return "No envelope yet";
}

function signatureLabel(status) {
  return (
    {
      completed: "Signed",
      signed: "Signed",
      declined: "Declined",
      voided: "Voided",
      delivered: "Awaiting",
      sent: "Sent",
      created: "Draft",
      not_started: "Not started",
    }[status] || status
  );
}

function signatureToBucket(status) {
  if (["completed", "signed"].includes(status)) return "complete";
  if (["declined", "voided", "failed"].includes(status)) return "failed";
  if (status === "not_started") return "not_started";
  return "in_progress";
}

function StatusCard({ icon: Icon, label, primary, secondary, status, statusLabel, accent, tileId, active, onClick, comingSoon = false }) {
  const accentBg = {
    indigo: "bg-indigo-50 border-indigo-200",
    blue: "bg-blue-50 border-blue-200",
    violet: "bg-violet-50 border-violet-200",
    emerald: "bg-emerald-50 border-emerald-200",
  }[accent || "indigo"];

  const activeRing = {
    indigo: "ring-2 ring-indigo-400 border-indigo-400",
    blue: "ring-2 ring-blue-400 border-blue-400",
    violet: "ring-2 ring-violet-400 border-violet-400",
    emerald: "ring-2 ring-emerald-500 border-emerald-500",
  }[accent || "indigo"];

  const iconColor = {
    indigo: "text-indigo-600 bg-indigo-100",
    blue: "text-blue-600 bg-blue-100",
    violet: "text-violet-600 bg-violet-100",
    emerald: "text-emerald-600 bg-emerald-100",
  }[accent || "indigo"];

  const statusPill = {
    complete: "bg-emerald-100 text-emerald-800 border-emerald-300",
    in_progress: "bg-amber-100 text-amber-800 border-amber-300",
    failed: "bg-red-100 text-red-800 border-red-300",
    not_started: "bg-slate-100 text-slate-600 border-slate-300",
    coming_soon: "bg-purple-100 text-purple-800 border-purple-300",
  }[status || "not_started"];

  const handleClick = () => {
    if (comingSoon) return;
    if (onClick && tileId) onClick(tileId);
  };

  return (
    <Card
      onClick={handleClick}
      className={
        `p-4 border ${accentBg} ${active ? activeRing : ""} relative overflow-hidden transition-shadow ` +
        (comingSoon ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:shadow-md")
      }
      data-testid={`status-card-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${iconColor}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span
          className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold border ${statusPill}`}
        >
          {statusLabel}
        </span>
      </div>
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
        {label}
      </p>
      <p className="font-semibold text-slate-900 text-sm truncate" title={primary}>
        {primary}
      </p>
      <p className="text-xs text-slate-600 truncate" title={secondary}>
        {secondary}
      </p>
      {/* Expand indicator (hidden when coming-soon) */}
      {!comingSoon && (
        <ChevronDown
          className={`absolute right-2 bottom-2 w-3.5 h-3.5 text-slate-400 transition-transform ${active ? "rotate-180" : ""}`}
        />
      )}
    </Card>
  );
}
